import { Session, User, AppSettings } from '../types';

const KEYS = {
  USER: '20min_user',
  SESSIONS: '20min_sessions',
  SETTINGS: '20min_settings',
};

// Storage helper to handle sandboxed environments where localStorage might be blocked
const storage = {
  getItem: (key: string): string | null => {
    try {
      return localStorage.getItem(key);
    } catch (e) {
      // Return null or check memory fallback
      return (window as any)._memStorage ? (window as any)._memStorage[key] : null;
    }
  },
  setItem: (key: string, value: string): void => {
    try {
      localStorage.setItem(key, value);
    } catch (e) {
      // Fallback to memory
      if (!(window as any)._memStorage) (window as any)._memStorage = {};
      (window as any)._memStorage[key] = value;
    }
  },
  removeItem: (key: string): void => {
    try {
      localStorage.removeItem(key);
    } catch (e) {
       if ((window as any)._memStorage) delete (window as any)._memStorage[key];
    }
  }
};

export const getTodayKey = (): string => {
  const d = new Date();
  return d.toISOString().split('T')[0];
};

export const saveUser = (user: User): void => {
  storage.setItem(KEYS.USER, JSON.stringify(user));
};

export const getUser = (): User | null => {
  const data = storage.getItem(KEYS.USER);
  return data ? JSON.parse(data) : null;
};

export const clearUser = (): void => {
  storage.removeItem(KEYS.USER);
};

export const saveSession = (session: Session): void => {
  const sessions = getSessions();
  sessions.push(session);
  storage.setItem(KEYS.SESSIONS, JSON.stringify(sessions));
};

export const getSessions = (): Session[] => {
  const data = storage.getItem(KEYS.SESSIONS);
  return data ? JSON.parse(data) : [];
};

export const clearSessions = (): void => {
  storage.removeItem(KEYS.SESSIONS);
};

export const getSessionsByDate = (dateKey: string): Session[] => {
  const sessions = getSessions();
  return sessions.filter(s => s.dateKey === dateKey);
};

export const getSettings = (): AppSettings => {
  const data = storage.getItem(KEYS.SETTINGS);
  if (data) {
    const parsed = JSON.parse(data) as Partial<AppSettings>;
    return {
      defaultInterval: parsed.defaultInterval ?? 20,
      soundEnabled: parsed.soundEnabled ?? true,
      volume: parsed.volume ?? 0.5,
      soundId: parsed.soundId ?? 'classic',
      theme: parsed.theme ?? 'light',
      newsletterOptIn: parsed.newsletterOptIn ?? false,
    };
  }
  return {
    defaultInterval: 20,
    soundEnabled: true,
    volume: 0.5,
    soundId: 'classic',
    theme: 'light',
    newsletterOptIn: false,
  };
};

export const saveSettings = (settings: AppSettings): void => {
  storage.setItem(KEYS.SETTINGS, JSON.stringify(settings));
};
